import React from "react";
import { onCreateSubmit, useApplyCreate } from "@hex-insights/app-modules";
import {
	Button,
	CardinalLocation,
	ClassNameProps,
	Column,
	compareObjects,
	compareStringsAsc,
	compareStringsDesc,
	count,
	Heading,
	If,
	makeClassName,
	makeObjectFromArrayMap,
	makeObjectFromEntries,
	Modal,
	ModalProps,
	Row,
	sort,
	StyleProps,
	Tooltip,
	TruncatedText,
} from "@hex-insights/core";
import {
	Form,
	FormState,
	FormSubmitFunction,
	FormTooltipButton,
	SubFormField,
	SubFormFieldItemsProps,
	SubmitButton,
	useFormState,
	useInternalFields,
	useOnSuccess,
} from "@hex-insights/forms";
import { Table } from "@hex-insights/tables";
import { Icon } from "../../../../Components/Icon";
import {
	AttendanceStatus,
	CourseSectionPeriod,
	SchoolAttendanceRecordListForCourseAttendanceQuery,
	Student,
	StudentAttendanceNotesListQuery,
	StudentNameAndImageFragment,
	useAttendanceStatusListQuery,
	useAttendanceStatusNamesQuery,
	useCourseAttendanceRecordBulkCreateMutation,
	useStudentNamesAndImagesQuery,
} from "../../../../Utilities";
import { StudentFormat } from "../../../Format";
import { CourseAttendanceRecordField } from "../../../Forms";
import { CourseAttendanceRecordFormConversion, CourseAttendanceRecordFormValues } from "../../../FormUtilities";
import {
	AttendanceStatusUtilities,
	SchoolAttendanceRecordUtilities,
	StudentAttendanceNoteUtilities,
	StudentUtilities,
} from "../../../Utilities";
import { StudentIcon } from "../../Student";
import { AttendanceNotesListModalController } from "../../StudentAttendanceNote";
import "./styles.css";
import styles from "./styles.module.css";

export type ControlledCourseAttendanceModalProps = Omit<
	CourseAttendanceModalProps,
	| "isLoadingStudents"
	| "students"
	| "isLoadingDefaultAttendanceStatus"
	| "defaultAttendanceStatusID"
	| "isLoadingSchoolAttendanceRecords"
	| "studentAttendanceRecordMap"
	| "isLoadingAttendanceNotes"
	| "studentAttendanceNotesMap"
	| "attendanceNotes"
>;

export function ControlledCourseAttendanceModal({
	courseSectionPeriodID,
	...props
}: ControlledCourseAttendanceModalProps) {
	const { loading: loadingStudents, data: studentData } = useStudentNamesAndImagesQuery({
		variables: {
			filters: StudentUtilities.getEnrolledInCourseSectionWithOnDateFilterInput(
				{ courseSectionPeriods: [{ idEQ: courseSectionPeriodID }] },
				props.date,
			),
		},
	});
	const students = React.useMemo(
		() =>
			studentData?.studentConnection.edges
				.map((e) => e.node)
				.sort(compareObjects((e) => StudentFormat.name(e), compareStringsAsc())),
		[studentData],
	);

	const { loading: loadingAttendanceStatus, data: attendanceStatusData } = useAttendanceStatusNamesQuery({
		variables: { filters: { isDefaultEQ: true }, pagination: { first: 1 } },
	});
	const attendanceStatusID = attendanceStatusData?.attendanceStatusConnection.edges[0]?.node?.id;

	const { schoolAttendanceRecordData, studentAttendanceRecordMap } =
		SchoolAttendanceRecordUtilities.useStudentSchoolAttendanceRecordOnDate(
			{ idIn: students?.map((e) => e.id) },
			props.date,
			!students,
		);

	const { attendanceNoteData, studentAttendanceNotesMap, attendanceNotes } =
		StudentAttendanceNoteUtilities.useStudentAttendanceNotesOnDate(
			{ idIn: students?.map((e) => e.id) },
			props.date,
			!students,
		);

	return (
		<CourseAttendanceModal
			courseSectionPeriodID={courseSectionPeriodID}
			isLoadingStudents={loadingStudents}
			students={students}
			isLoadingDefaultAttendanceStatus={loadingAttendanceStatus}
			defaultAttendanceStatusID={attendanceStatusID}
			isLoadingSchoolAttendanceRecords={!schoolAttendanceRecordData}
			studentAttendanceRecordMap={studentAttendanceRecordMap}
			isLoadingAttendanceNotes={!attendanceNoteData}
			studentAttendanceNotesMap={studentAttendanceNotesMap}
			attendanceNotes={attendanceNotes}
			{...props}
		/>
	);
}

export type CourseAttendanceModalProps = {
	courseSectionPeriodID: CourseSectionPeriod["id"];
	isLoadingStudents?: boolean;
	students: (Pick<Student, "id"> & StudentNameAndImageFragment)[] | null | undefined;
	isLoadingDefaultAttendanceStatus?: boolean;
	defaultAttendanceStatusID: AttendanceStatus["id"] | null | undefined;
	isLoadingSchoolAttendanceRecords?: boolean;
	studentAttendanceRecordMap:
		| Record<
				Student["id"],
				SchoolAttendanceRecordListForCourseAttendanceQuery["schoolAttendanceRecordConnection"]["edges"][0]["node"]
		  >
		| null
		| undefined;
	isLoadingAttendanceNotes?: boolean;
	studentAttendanceNotesMap:
		| Record<Student["id"], StudentAttendanceNotesListQuery["studentNoteConnection"]["edges"][0]["node"][]>
		| null
		| undefined;
	attendanceNotes: StudentAttendanceNotesListQuery["studentNoteConnection"]["edges"][0]["node"][] | null | undefined;
	date: string;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function CourseAttendanceModal({
	courseSectionPeriodID,
	isLoadingStudents = false,
	students,
	isLoadingDefaultAttendanceStatus = false,
	defaultAttendanceStatusID,
	isLoadingSchoolAttendanceRecords = false,
	studentAttendanceRecordMap,
	isLoadingAttendanceNotes = false,
	studentAttendanceNotesMap,
	attendanceNotes,
	date,
	ifRef,
	onClose,
	className,
	style,
}: CourseAttendanceModalProps) {
	const initialFormValues = React.useMemo(
		() => ({
			courseAttendanceRecords: studentsToFormValuesSets(
				students,
				courseSectionPeriodID,
				defaultAttendanceStatusID,
				studentAttendanceRecordMap,
				studentAttendanceNotesMap,
				date,
			),
		}),
		[
			students,
			courseSectionPeriodID,
			defaultAttendanceStatusID,
			studentAttendanceRecordMap,
			studentAttendanceNotesMap,
			date,
		],
	);

	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal size="large" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Take Attendance</Heading.H2>
			</Modal.Header>

			<If
				condition={
					isLoadingStudents ||
					isLoadingDefaultAttendanceStatus ||
					isLoadingSchoolAttendanceRecords ||
					isLoadingAttendanceNotes
				}
			>
				<Modal.Body>Loading...</Modal.Body>
			</If>

			{!isLoadingStudents &&
				!isLoadingDefaultAttendanceStatus &&
				!isLoadingSchoolAttendanceRecords &&
				!isLoadingAttendanceNotes &&
				students &&
				defaultAttendanceStatusID &&
				studentAttendanceRecordMap &&
				studentAttendanceNotesMap &&
				attendanceNotes && (
					<BulkCreateForm
						initialFormValues={initialFormValues}
						students={students}
						studentAttendanceNotesMap={studentAttendanceNotesMap}
						attendanceNotes={attendanceNotes}
						date={date}
						onSuccess={onSuccess}
					/>
				)}
		</Modal>
	);
}

type CourseAttendanceRecordBulkCreateFormValues = {
	courseAttendanceRecords: CourseAttendanceRecordFormValues.Create[];
};

type BulkCreateFormProps = {
	initialFormValues: CourseAttendanceRecordBulkCreateFormValues;
	students: (Pick<Student, "id"> & StudentNameAndImageFragment)[];
	studentAttendanceNotesMap: Record<
		Student["id"],
		StudentAttendanceNotesListQuery["studentNoteConnection"]["edges"][0]["node"][]
	>;
	attendanceNotes: StudentAttendanceNotesListQuery["studentNoteConnection"]["edges"][0]["node"][];
	date: string;
	onSuccess: () => void;
};

function BulkCreateForm({
	initialFormValues,
	students,
	studentAttendanceNotesMap,
	attendanceNotes,
	onSuccess,
}: BulkCreateFormProps) {
	const studentIDMap = React.useMemo(() => makeObjectFromArrayMap(students, (e) => [e.id, e]), [students]);

	const formState = useFormState({ initialFormValues });

	const [create] = useCourseAttendanceRecordBulkCreateMutation();
	const wrappedCreate = React.useCallback(
		async (formValues: CourseAttendanceRecordBulkCreateFormValues) => {
			const inputs = formValues.courseAttendanceRecords.map((e) =>
				CourseAttendanceRecordFormConversion.toGQLCreateInput(e),
			);
			const { data, errors, ...result } = await create({
				variables: { inputs },
				update: (cache) => {
					cache.evict({ id: "ROOT_QUERY", fieldName: "courseAttendanceRecordConnection" });
					cache.evict({ id: "ROOT_QUERY", fieldName: "courseSectionPeriodConnection" });
				},
			});
			return { ...result, data: data ?? null, errors: errors ?? null };
		},
		[create],
	);
	const { applyCreate } = useApplyCreate(wrappedCreate);
	const onSubmit = React.useCallback<FormSubmitFunction<CourseAttendanceRecordBulkCreateFormValues>>(
		async ({ formValues }) => onCreateSubmit(formValues, applyCreate),
		[applyCreate],
	);
	useOnSuccess(formState, onSuccess);

	const { data: attendanceStatusData } = useAttendanceStatusListQuery();
	const presentAttendanceStatuses = makeObjectFromEntries(
		attendanceStatusData?.attendanceStatusConnection.edges.map((e) => [
			e.node.id,
			AttendanceStatusUtilities.isPresent(e.node),
		]) ?? [],
	);

	const numPresent = !!attendanceStatusData
		? count(formState.formValues.courseAttendanceRecords, (e) => presentAttendanceStatuses[e.attendanceStatusID ?? ""])
		: formState.formValues.courseAttendanceRecords.length;
	const numStudents = formState.formValues.courseAttendanceRecords.length;

	return (
		<React.Fragment>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem" style={{ height: "100%" }}>
					<Row justify="spaced-start" horizontalSpacing="2rem" align="center">
						<Column justify="spaced-start" verticalSpacing="0.25rem">
							<span className={styles["summary__label"]}>Students Present</span>
							<span className={styles["summary__value"]}>
								{numPresent} <span className={styles["summary__value__denominator"]}>/ {numStudents}</span>
							</span>
						</Column>

						<If condition={attendanceNotes.length > 0}>
							<AttendanceNotesListModalController attendanceNotes={attendanceNotes} />
						</If>
					</Row>

					<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} style={{ height: "100%" }}>
						<Form.Column style={{ width: "100%", height: "100%" }}>
							<SubFormField
								formState={formState}
								name="courseAttendanceRecords"
								blankItem={CourseAttendanceRecordFormValues.initialCreate}
								noHeader
								Items={CourseAttendanceRecordFormItems}
								style={{ width: "100%", height: "100%" }}
								contentClassName={styles["form__content"]}
								className="course-attendance-modal__form__sub-form-field"
							>
								{({ formState }) => (
									<ItemCreateFields
										formState={formState}
										student={studentIDMap[formState.formValues.studentID ?? ""] ?? {}}
										attendanceNotes={studentAttendanceNotesMap[formState.formValues.studentID ?? ""] ?? []}
										style={{ width: "100%" }}
									/>
								)}
								{() => <div></div>}
							</SubFormField>
						</Form.Column>
					</Form>
				</Column>
			</Modal.Body>
			<Modal.Footer className={styles["modal__footer"]}>
				<Row justify="flex-end">
					<SubmitButton onClick={formState.onSubmitWrapper(onSubmit)} submissionStatus={formState.submissionStatus}>
						Take Attendance
					</SubmitButton>
				</Row>
			</Modal.Footer>
		</React.Fragment>
	);
}

const columns = ["student", "attendanceStatus", "notes", "attendanceNotes"];

const columnWidths = {
	attendanceNotes: "3rem",
};

function CourseAttendanceRecordFormItems({
	children,
}: SubFormFieldItemsProps<CourseAttendanceRecordBulkCreateFormValues, CourseAttendanceRecordFormValues.Create>) {
	return (
		<Table columns={columns} columnWidths={columnWidths} className={styles["table"]}>
			<Table.Header>
				<Table.Row>
					<Table.Heading column="student" noResize>
						Student
					</Table.Heading>
					<Table.Heading column="attendanceStatus" noResize>
						Attendance Status
					</Table.Heading>
					<Table.Heading column="notes" noResize>
						Notes
					</Table.Heading>
					<Table.Heading column="attendanceNotes" noResize></Table.Heading>
				</Table.Row>
			</Table.Header>
			<Table.Body style={{ height: "100%" }}>{children}</Table.Body>
		</Table>
	);
}

function studentsToFormValuesSets(
	students: Pick<Student, "id">[] | null | undefined,
	courseSectionPeriodID: CourseSectionPeriod["id"],
	attendanceStatusID: AttendanceStatus["id"] | null | undefined,
	studentAttendanceRecordMap:
		| Record<
				Student["id"],
				SchoolAttendanceRecordListForCourseAttendanceQuery["schoolAttendanceRecordConnection"]["edges"][0]["node"]
		  >
		| null
		| undefined,
	studentAttendanceNotesMap:
		| Record<Student["id"], StudentAttendanceNotesListQuery["studentNoteConnection"]["edges"][0]["node"][]>
		| null
		| undefined,
	attendanceDate: string,
) {
	return (
		students?.map((s) => {
			const attendanceStatusIDFromSchoolAttendanceRecord = studentAttendanceRecordMap?.[s.id]?.attendanceStatusID;

			const attendanceNotes = sort(
				(studentAttendanceNotesMap?.[s.id] ?? []).filter((e) => !!e.studentAttendanceNote?.attendanceStatus),
				compareObjects((e) => e.createdAt, compareStringsDesc()),
			);
			const attendanceStatusIDFromNotes = attendanceNotes[0]?.studentAttendanceNote?.attendanceStatus?.id;

			return {
				...CourseAttendanceRecordFormValues.initialCreate,
				studentID: s.id,
				courseSectionPeriodID,
				attendanceStatusID:
					attendanceStatusIDFromSchoolAttendanceRecord ?? attendanceStatusIDFromNotes ?? attendanceStatusID ?? null,
				attendanceDate,
			};
		}) ?? []
	);
}

const internalFields: (keyof CourseAttendanceRecordFormValues.Create)[] = [
	"studentID",
	"courseSectionPeriodID",
	"attendanceDate",
];

const notesTooltipSideOrder: CardinalLocation[] = ["bottom", "top", "right", "left"];

export type ItemCreateFieldsProps = {
	formState: FormState<CourseAttendanceRecordFormValues.Create>;
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	attendanceNotes: StudentAttendanceNotesListQuery["studentNoteConnection"]["edges"][0]["node"][];
} & Partial<ClassNameProps & StyleProps>;

export function ItemCreateFields({ formState, student, attendanceNotes, className, style }: ItemCreateFieldsProps) {
	useInternalFields(formState, internalFields);

	const { notes } = formState.formValues;

	const [notesDisplay, setNotesDisplay] = React.useState("");
	const onNotesTooltipOpenChange = React.useCallback(
		(isOpen: boolean) => {
			if (!isOpen) {
				setNotesDisplay(notes);
			}
		},
		[notes],
	);

	return (
		<Table.Row className={makeClassName(styles["table__row"], className)} style={style}>
			<Table.Cell column="student" oneLine>
				<Row justify="spaced-start" align="center" className={styles["table__cell__row"]}>
					<StudentIcon student={student} size={24} />
					<TruncatedText style={{ fontSize: "0.875rem" }}>{StudentFormat.name(student)}</TruncatedText>
				</Row>
			</Table.Cell>

			<Table.Cell column="attendanceStatus" className={styles["table__cell--attendance-status"]}>
				<CourseAttendanceRecordField.AttendanceStatus.Select
					formState={formState}
					noHeader
					className={styles["attendance-status-select"]}
				/>
			</Table.Cell>

			<Table.Cell column="notes" className={styles["table__cell--notes"]}>
				<Row justify="spaced-start" align="center" className={styles["table__cell__row"]}>
					{!!notesDisplay && (
						<TruncatedText style={{ fontSize: "0.875rem", minWidth: 0 }}>{notesDisplay.split("\n")[0]}</TruncatedText>
					)}

					<FormTooltipButton
						formState={formState}
						sideOrder={notesTooltipSideOrder}
						autoFocus
						onOpenChange={onNotesTooltipOpenChange}
						style={{ width: "fit-content" }}
					>
						{({ formState, close }) => (
							<Tooltip isInteractable>
								<Tooltip.Body>
									<Column justify="spaced-start">
										<CourseAttendanceRecordField.Notes formState={formState} />

										<Row justify="flex-end">
											<Button variant="primary" onClick={close}>
												Done
											</Button>
										</Row>
									</Column>
								</Tooltip.Body>
							</Tooltip>
						)}
						{({ onClick }) => {
							const hasNote = notesDisplay !== "";

							return (
								<Button variant="link" size="small" onClick={onClick}>
									{hasNote ? (
										<Icon.PencilSimple size={16} className="block" />
									) : (
										<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
											<Icon.Plus size={16} /> Add Note
										</Row>
									)}
								</Button>
							);
						}}
					</FormTooltipButton>
				</Row>
			</Table.Cell>

			<Table.Cell column="attendanceNotes">
				<If condition={attendanceNotes.length > 0}>
					<Row justify="center" align="center" className={styles["table__cell__row"]}>
						<AttendanceNotesListModalController attendanceNotes={attendanceNotes} slim />
					</Row>
				</If>
			</Table.Cell>
		</Table.Row>
	);
}
